import React, { Dispatch, SetStateAction, useMemo, useState } from 'react'

import {
  CheckIcon,
  ChevronRightIcon,
  ExclamationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid'

import LoadingIcon from '@/components/LoadingIcon'
import { useCurrentApplication } from '@/graphql/services/application'
import {
  useDeleteChecklistItemMutation,
  useUpdateChecklistItemMutation,
  useUpdateTaskAuditMutation,
} from '@/services/application'
import { useGetLoggedUserQuery } from '@/services/user'
import { useAuditAllDoneItemsFromGroupMutation } from '@/services/v2-application/checklistGroup'
import { useGetChecklistGroupsQuery } from '@/services/v2-application/checklistGroup'
import { ChecklistItem } from '@/types/unlock'
import { isOpsManager } from '@/utils/user'

export type TaskDotsMenuProps = {
  task: ChecklistItem
  setShowingDotsMenu: Dispatch<SetStateAction<boolean>>
  setEditingNotes: Dispatch<SetStateAction<boolean>>
  toggleTask: (task: ChecklistItem) => void
}

export const TaskDotsMenu = (props: TaskDotsMenuProps) => {
  const { task, setShowingDotsMenu, setEditingNotes, toggleTask } = props
  const [deleteChecklistItem] = useDeleteChecklistItemMutation()
  const [updateChecklistItem] = useUpdateChecklistItemMutation()
  const [updateTaskAudit, { isLoading }] = useUpdateTaskAuditMutation()
  const [updateAuditAllChecklistGroup] = useAuditAllDoneItemsFromGroupMutation()

  const [showingQueuesMenu, setShowingQueuesMenu] = useState(false)
  const [isTaskAudit, setIsTaskAudit] = useState(false)

  const { data: application } = useCurrentApplication()

  const { data: checklist_groups } = useGetChecklistGroupsQuery(application?.uuid, {
    skip: !application,
  })

  const { data: user } = useGetLoggedUserQuery()

  const WorkQueuesMenu = useMemo(() => {
    const changeGroup = (newGroup: number) => {
      if (task.checklist_group !== newGroup) {
        updateChecklistItem({ ...task, checklist_group: newGroup })
      }
      setShowingDotsMenu(false)
    }

    return (
      <div className='px-px py-4 border border-gray-200 rounded-md bg-white w-max'>
        {checklist_groups?.results?.map((group) => {
          const isCurrent = task.checklist_group === group.id

          return (
            <div
              key={group.id}
              className={`flex items-center cursor-pointer px-4 hover:bg-gray-400 ${
                isCurrent ? 'bg-gray-400' : ''
              }`}
              onClick={() => changeGroup(group.id)}
            >
              <div className={`w-[20px] shrink-0`}>
                {isCurrent ? <CheckIcon width={12} /> : null}
              </div>
              <p className='text-[12px]'>{group.type_name}</p>
            </div>
          )
        })}
      </div>
    )
  }, [checklist_groups?.results, setShowingDotsMenu, task, updateChecklistItem])

  const TaskAuditMenu = useMemo(() => {
    const handleUpdateTaskAudit = async (isAuditPass: boolean) => {
      await updateTaskAudit({ ...task, is_audit_pass: isAuditPass })
      setShowingDotsMenu(false)
    }
    const handleGroupAuditAll = async () => {
      const checklistGroupUUID = checklist_groups?.results?.find(
        (group) => group.id === task.checklist_group
      )?.uuid
      await updateAuditAllChecklistGroup({ applicationId: task.application, checklistGroupUUID })
      setShowingDotsMenu(false)
    }

    return (
      <div className='px-px py-4 border border-gray-200 rounded-md bg-white w-[150px] h-[102px]'>
        {isLoading ? (
          <div className='flex justify-center items-center'>
            <LoadingIcon />
          </div>
        ) : (
          <>
            <div
              className={`flex items-center cursor-pointer  px-4 hover:bg-gray-400 mb-[7px]`}
              onClick={() => handleGroupAuditAll()}
            >
              <div className='w-2/12'>
                <div
                  className={`flex shrink-0 items-center justify-center border rounded-full cursor-pointer bg-sky-400 w-4`}
                >
                  <ExclamationCircleIcon
                    fill='blue'
                    stroke='white'
                    width={15}
                  />
                </div>
              </div>
              <div className='w-10/12'>
                <p className='text-[12px]'>Pass All Tasks</p>
              </div>
            </div>
            <div
              className={`flex items-center cursor-pointer  px-4 hover:bg-gray-400 mb-[7px]`}
              onClick={() => handleUpdateTaskAudit(true)}
            >
              <div className='w-2/12'>
                <div
                  className={`flex shrink-0 items-center justify-center border rounded-full cursor-pointer bg-success w-4`}
                >
                  <CheckIcon
                    fill='white'
                    width={15}
                  />
                </div>
              </div>
              <div className='w-10/12'>
                <p className='text-[12px]'>Pass this task</p>
              </div>
            </div>
            <div
              className={`flex items-center cursor-pointer px-4 hover:bg-gray-400`}
              onClick={() => handleUpdateTaskAudit(false)}
            >
              <div className='w-2/12'>
                <div className={`flex shrink-0 cursor-pointer`}>
                  <XMarkIcon
                    fill='red'
                    width={15}
                  />
                </div>
              </div>
              <div className='w-10/12'>
                <p className='text-[12px]'>Fail this task</p>
              </div>
            </div>
          </>
        )}
      </div>
    )
  }, [])

  return (
    <div className='px-3 py-1 border border-gray-200 rounded-md bg-white w-max'>
      {showingQueuesMenu && <div className='absolute left-0 top-0 z-10'>{WorkQueuesMenu}</div>}
      {isTaskAudit && <div className='absolute left-0 top-0 z-10'>{TaskAuditMenu}</div>}

      {isOpsManager(user) && task?.status == 'done' && (
        <div
          className='flex gap-1 hover:bg-sky-300 cursor-pointer'
          onClick={() => {
            setIsTaskAudit(true)
          }}
        >
          <p className='p-px text-sky-400 text-[12px]'>Task Audit</p>
          <ChevronRightIcon width={10} />
        </div>
      )}

      <div
        className='flex gap-1 hover:bg-sky-300 cursor-pointer'
        onClick={() => {
          setShowingQueuesMenu(true)
        }}
      >
        <p className='p-px text-sky-400 text-[12px]'>Assign Work Queue</p>
        <ChevronRightIcon width={10} />
      </div>

      <p
        className='p-px text-sky-400 text-[12px] hover:bg-sky-300 cursor-pointer'
        onClick={() => {
          setShowingDotsMenu(false)
          setEditingNotes(true)
        }}
      >
        Add Note
      </p>
      <p
        className='p-px text-sky-400 text-[12px] hover:bg-sky-300 cursor-pointer'
        onClick={() => {
          setShowingDotsMenu(false)
          toggleTask(task)
        }}
      >
        {task.status !== 'done' ? 'Mark as Complete' : 'Mark as Uncompleted'}
      </p>
      <p
        className='p-px text-error text-[12px] hover:bg-gray-400 cursor-pointer'
        onClick={() => {
          deleteChecklistItem(task.uuid)
        }}
      >
        Delete
      </p>
    </div>
  )
}
